import { useEffect, useState } from "react";
import coverFontUrl from "../fonts/HalvarBreit-Blk.ttf";
import { resolveCoverDateMode } from "../coverDate.js";

const FONT = "CoverPdfHalvar";
const CAP = 0.656; // sCapHeight / unitsPerEm из приложенного HalvarBreit-Blk.ttf
let fontPromise;
function ensureFont() {
  if (!fontPromise) {
    const face = new FontFace(FONT, `url(${coverFontUrl})`, { weight: "800" });
    fontPromise = face.load().then(loaded => { document.fonts.add(loaded); });
  }
  return fontPromise;
}

// Повторяет _calc_title_layout: переносы, min/max, окончательное сжатие.
export function pdfTitleLayout(text, layout, measure) {
  const maxCap = layout.titleMaxCap, minCap = 8, gap = 2, blockH = layout.titleH;
  const width = layout.sideW - 2 * layout.safe;
  let lines = String(text || "").split("\n").map(s => s.trim().toUpperCase()).filter(Boolean);
  if (!lines.length) lines = [""];
  const capFor = line => { const w = measure(line, 100); return w > 0 ? width / w * 100 * CAP : maxCap; };
  const bestCap = rows => Math.min(...rows.map(capFor));
  let cap = Math.min(bestCap(lines), maxCap);
  if (cap < minCap && lines.length === 1) {
    let words = lines[0].split(/\s+/).filter(Boolean);
    if (words.length < 2 && lines[0].includes("-")) {
      const chunks = lines[0].split("-");
      words = chunks.map((s, i) => i < chunks.length - 1 ? s + "-" : s).filter(Boolean);
    }
    let best = null, score = 0;
    for (let i = 1; i < words.length; i++) {
      const rows = [words.slice(0, i).join(" "), words.slice(i).join(" ")];
      const value = Math.min(bestCap(rows), maxCap);
      if (value > score) { best = rows; score = value; }
    }
    if (best) { lines = best; cap = score; }
  }
  const maxByHeight = (blockH - (lines.length - 1) * gap) / lines.length;
  cap = Math.min(cap, maxByHeight);
  if (cap < minCap && minCap <= maxByHeight) cap = minCap;
  cap = Math.max(1, cap);
  const fontSize = cap / CAP;
  const maxWidth = Math.max(...lines.map(s => measure(s, fontSize)));
  let scaleX = maxWidth > width ? width / maxWidth : 1;
  let scaleY = Math.min(1, blockH / (lines.length * fontSize + (lines.length - 1) * gap));
  if (scaleY < 1) scaleX = scaleY = Math.min(scaleX, scaleY);
  return { lines, fontSize, scaleX, scaleY, gap };
}

export default function PdfCoverText({ layout, title, year, dateMode = "year", showYear = true,
  titleColor = "#fff", yearColor = "#fff" }) {
  const [fontReady, setFontReady] = useState(false);
  useEffect(() => {
    let active = true;
    ensureFont().then(() => { if (active) setFontReady(true); }).catch(error => {
      console.error("Не загружен шрифт обложки", error);
    });
    return () => { active = false; };
  }, []);
  if (!fontReady) return null;
  const ctx = document.createElement("canvas").getContext("2d");
  ctx.fontKerning = "none";
  const measure = (text, size) => { ctx.font = `800 1000px ${FONT}`; return ctx.measureText(text).width * size / 1000; };
  const displayTitle = title || "Тут текст";
  const t = pdfTitleLayout(displayTitle, layout, measure);
  const titleBottom = layout.safe + layout.titleH;
  const date = String(year || "").trim();
  const baseDateFont = layout.yearH / CAP;
  const naturalDateWidth = measure(date, baseDateFont);
  const fitDate = ["date", "range"].includes(resolveCoverDateMode(dateMode, date));
  const dateScale = fitDate && naturalDateWidth > 0
    ? Math.min(1, layout.yearMaxW / naturalDateWidth) : 1;
  const dateFont = baseDateFont * dateScale;
  const dateWidth = fitDate
    ? naturalDateWidth * dateScale : layout.yearW;
  return (
    <svg viewBox={`0 0 ${layout.sideW} ${layout.sideH}`} aria-hidden="true"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 2, fontKerning: "none", fontVariantLigatures: "none", whiteSpace: "pre" }}>
      {t.lines.map((line, i) => (
        <text key={i} x="0" y="0" textAnchor="middle" fill={titleColor}
          textLength={measure(line, t.fontSize)} lengthAdjust="spacingAndGlyphs"
          opacity={title ? 1 : 0.45} fontFamily={FONT} fontWeight="800" fontSize={t.fontSize}
          transform={`translate(${layout.sideW / 2} ${titleBottom - (t.lines.length - 1 - i) * (t.fontSize + t.gap) * t.scaleY}) scale(${t.scaleX} ${t.scaleY})`}>
          {line}
        </text>
      ))}
      {showYear && date && (
        <text x={layout.sideW / 2} y={titleBottom + layout.yearGap + layout.yearH}
          textAnchor="middle" fontFamily={FONT} fontWeight="800" fontSize={dateFont}
          textLength={dateWidth} lengthAdjust="spacingAndGlyphs" fill={yearColor}>
          {date}
        </text>
      )}
    </svg>
  );
}